import { useCallback, useEffect, useState } from "react"
import api from "../utils/api"
import { getApiErrorMessage } from "../utils/errorMessage"
import { formatDateWithTime, formatMoney, getNairobiMonth, getNairobiYear } from "../utils/format"
import Icon from "./Icon"
import MonthlySummaryCard from "./MonthlySummaryCard"
import StatementTable from "../pages/utbills/StatementTable"
import { getUtilityStatementDate } from "../pages/frp/frpStatements"
import UtilityInvoiceStatusBadge from "../pages/utbills/UtilityInvoiceStatusBadge"
import {
  getUtilityRowChargeAmount,
  getUtilityRowPaymentAmount,
  getUtilitySettlementId,
  getVisibleUtilitySettlementRows,
  isUtilityMeterRow,
} from "../pages/utbills/utilitySettlement"

// Data-loading wrapper around MonthlySummaryCard. Point it at an endpoint that
// returns { period, sections: [{ role, stats:[...], facts?:{...} }] } and it
// handles month/year state, fetching, and shape-mapping. The "Transactions"
// button loads `${endpoint}/transactions?month&year&role=` for the active
// section and lists them in a modal. Used by Groups and Utilities.
function MonthlySummaryPanel({
  endpoint,
  buildTransactionsUrl,
  title,
  subtitle,
  icon,
  accent = "sky",
  emptyText,
  // When set, only the section whose `role` matches is shown (and the section
  // switcher is hidden). Used to scope the card to the active module tab.
  sectionKey,
  renderExtraTile,
  refreshKey,
  // Opt-in: only a view that has both money in and money out asks for the cash
  // Balance column in the transactions modal.
  showBalanceColumn = false,
}) {
  const [month, setMonth] = useState(getNairobiMonth())
  const [year, setYear] = useState(getNairobiYear())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [data, setData] = useState(null)

  // Transactions modal state
  const [txOpen, setTxOpen] = useState(false)
  const [txLoading, setTxLoading] = useState(false)
  const [txError, setTxError] = useState("")
  const [txRows, setTxRows] = useState([])
  const [txRole, setTxRole] = useState("")

  const fetchSummary = useCallback(
    async (m = month, y = year) => {
      setLoading(true)
      setError("")
      try {
        const res = await api.get(`${endpoint}?month=${m}&year=${y}`)
        setData(res?.data || null)
      } catch (err) {
        setError(getApiErrorMessage(err, "Failed to load monthly summary."))
        setData(null)
      } finally {
        setLoading(false)
      }
    },
    [endpoint, month, year]
  )

  useEffect(() => {
    fetchSummary(month, year)
    // Initial load only; subsequent refreshes go through the Update button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (refreshKey === undefined || refreshKey === null) return
    fetchSummary(month, year)
    // External refresh only; month/year changes still wait for the Update button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey])

  const viewTransactions = useCallback(
    async (role) => {
      setTxOpen(true)
      setTxRole(role)
      setTxLoading(true)
      setTxError("")
      setTxRows([])
      try {
        const url = typeof buildTransactionsUrl === "function"
          ? buildTransactionsUrl({ endpoint, month, year, role })
          : `${endpoint}/transactions?month=${month}&year=${year}&role=${encodeURIComponent(role)}`
        const res = await api.get(url)
        setTxRows(extractTransactionRows(res?.data, { month, year }))
      } catch (err) {
        setTxError(getApiErrorMessage(err, "Failed to load transactions."))
      } finally {
        setTxLoading(false)
      }
    },
    [buildTransactionsUrl, endpoint, month, year]
  )

  const sections = (data?.sections || [])
    .filter((section) => !sectionKey || section.role === sectionKey)
    .map((section) => ({
      key: section.role,
      label: section.role,
      stats: section.stats || [],
      summary: section.summary || {},
      facts: section.facts
        ? Object.entries(section.facts).map(([label, value]) => ({ label, value }))
        : undefined,
    }))

  return (
    <div>
      {error && (
        <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
          {error}
        </div>
      )}
      <MonthlySummaryCard
        title={title}
        subtitle={subtitle}
        icon={icon}
        accent={accent}
        month={month}
        year={year}
        period={data?.period}
        loading={loading}
        onMonthChange={setMonth}
        onYearChange={setYear}
        onUpdate={() => fetchSummary(month, year)}
        sections={sections}
        emptyText={emptyText}
        onViewTransactions={viewTransactions}
        renderExtraTile={renderExtraTile}
      />

      {txOpen && (
        <TransactionsModal
          title={`${txRole} — ${data?.period || ""}`}
          rows={txRows}
          loading={txLoading}
          error={txError}
          showBalanceColumn={showBalanceColumn}
          onClose={() => setTxOpen(false)}
        />
      )}
    </div>
  )
}

function extractTransactionRows(data, period = {}) {
  if (Array.isArray(data?.transactions)) return data.transactions
  if (Array.isArray(data?.statement?.rows)) {
    const invoicesById = new Map(
      (Array.isArray(data?.statement?.invoices) ? data.statement.invoices : [])
        .map((invoice) => [String(invoice?._id || invoice?.invoiceId || ""), invoice]),
    )
    return getVisibleUtilitySettlementRows(data.statement.rows)
      .filter((row) => isStatementRowInPeriod(row, period))
      .map((row) => {
        const invoice = row?.type === "invoice"
          ? invoicesById.get(String(row?.invoiceId || ""))
          : null
        return mapStatementRowToTransaction(invoice ? {
          ...row,
          cashPaidAmount: invoice.cashPaidAmount,
          waivedAmount: invoice.waivedAmount,
          creditAppliedAmount: invoice.creditAppliedAmount,
          creditMovedAmount: invoice.creditMovedAmount,
          totalSettledAmount: invoice.totalSettledAmount,
          outstandingAmount: invoice.outstandingAmount,
          settlementStatus: invoice.settlementStatus,
          isOverdue: invoice.isOverdue,
          dueDate: invoice.dueDate,
          status: invoice.status,
        } : row)
      })
  }
  return []
}

function isStatementRowInPeriod(row, { month, year } = {}) {
  if (!month || !year) return true
  const d = new Date(getUtilityStatementDate(row) || 0)
  if (Number.isNaN(d.getTime())) return false
  return d.getFullYear() === Number(year) && d.getMonth() + 1 === Number(month)
}

function titleFromStatementRow(row) {
  return String(row?.description || row?.providerName || "Utility record").trim()
}

function subtitleFromStatementRow(row) {
  return [
    row?.providerName,
    row?.billNumber ? `Bill ${row.billNumber}` : "",
    row?.source ? String(row.source).replace(/_/g, " ") : "",
  ].filter(Boolean).join(" | ")
}

function mapStatementRowToTransaction(row) {
  const type = String(row?.type || "").trim().toLowerCase()
  const isMeterEvent = isUtilityMeterRow(row)
  const chargeAmount = getUtilityRowChargeAmount(row)
  const paymentAmount = getUtilityRowPaymentAmount(row)
  const walletDelta = Number(row?.walletDelta)
  const amount = chargeAmount > 0 ? chargeAmount : paymentAmount
  const eventType =
    type === "invoice" ? (row?.status === "overdue" ? "overdue_invoice" : row?.status === "paid" ? "invoice_paid" : "invoice") :
    type === "deposit" ? "topup" :
    type === "utility_payment" ? "payment" :
    type === "water_top_up" ? "water_meter" :
    type === "reading_update" ? "reading_update" :
    type === "opening_balance" ? "adjustment" :
    type || "adjustment"
  const direction =
    chargeAmount > 0 ? "charge" :
    type === "payment" || type === "utility_payment" ? "out" :
    // A credit row only notes that overpaid cash became credit; no money moves.
    isMeterEvent || type === "opening_balance" || type === "credit" ? "neutral" :
    Number.isFinite(walletDelta) && walletDelta < 0 ? "out" :
    Number.isFinite(walletDelta) && walletDelta > 0 ? "in" :
    "in"

  const statementDate = getUtilityStatementDate(row)
  return {
    id: row?._id || `${type || "row"}-${row?.date || row?.sortTimestamp || ""}`,
    date: statementDate,
    sortTimestamp: statementDate,
    description: titleFromStatementRow(row),
    type,
    source: row?.source || "",
    chargeAmount,
    paymentAmount,
    title: titleFromStatementRow(row),
    subtitle: subtitleFromStatementRow(row),
    amount,
    direction,
    eventType,
    status: row?.status,
    settlementId: getUtilitySettlementId(row),
    settlementStatus: row?.settlementStatus,
    allocations: row?.allocations || row?.settlement?.allocations || [],
    cashPaidAmount: row?.cashPaidAmount,
    waivedAmount: row?.waivedAmount,
    creditAppliedAmount: row?.creditAppliedAmount,
    creditMovedAmount: row?.creditMovedAmount,
    totalSettledAmount: row?.totalSettledAmount,
    outstandingAmount: row?.outstandingAmount,
    isOverdue: row?.isOverdue,
    invoiceId: row?.invoiceId,
    dueDate: row?.dueDate,
    balanceAfter: row?.balanceAfter,
    balanceScope: row?.balanceScope,
    meta: row?.meta,
  }
}

function formatTransactionType(row) {
  return String(row?.type || row?.eventType || "event").replace(/_/g, " ")
}

function normalizeTransactionTableRow(row) {
  const amount = Number(row?.amount || 0)
  const isExpense =
    row?.eventType === "expense" || row?.type === "expense" || row?.source === "utility_expense"
  const isReversal = row?.eventType === "reversal" || row?.type === "reversal"
  const rawCharge = Number.isFinite(Number(row?.chargeAmount))
    ? Number(row.chargeAmount)
    : row?.direction === "charge" || isExpense || isReversal
      ? amount
      : 0
  const paymentAmount = isUtilityMeterRow(row) || isReversal
    ? 0
    : Number.isFinite(Number(row?.paymentAmount))
    ? Number(row.paymentAmount)
    : !isExpense && row?.direction && row.direction !== "charge" && row.direction !== "neutral"
      ? amount
      : 0
  // Expenses get their own column; keep them out of "Charge" so the two never
  // double-count the same amount.
  const expenseAmount = isExpense ? rawCharge || amount : 0
  const chargeAmount = isExpense ? 0 : rawCharge

  return {
    ...row,
    description: row?.description || row?.title || "Utility record",
    type: formatTransactionType(row),
    chargeAmount,
    expenseAmount,
    paymentAmount,
    displayDate: row?.sortTimestamp || row?.date,
  }
}

// This modal is a statement for one month only, so its balance always opens at
// zero on the first day of that month and moves only with the records shown.
// It tracks cash: money received less money paid out. An invoice is a promise,
// not cash, so it never moves this balance. Kept the same way round as the
// "Net cash" tile on the summary card so the two figures always agree.
// Rows arrive newest first, so the walk starts at the end of the list.
function withMonthlyRunningBalances(rows = []) {
  let runningBalance = 0
  const balances = new Array(rows.length)

  for (let index = rows.length - 1; index >= 0; index -= 1) {
    runningBalance += Number(rows[index].paymentAmount || 0) - Number(rows[index].expenseAmount || 0)
    balances[index] = Math.round(runningBalance * 100) / 100
  }

  return rows.map((row, index) => ({ ...row, runningBalance: balances[index] }))
}

function TransactionSourceTag({ row }) {
  if (row?.source === "utility_expense" || row?.eventType === "expense") {
    return (
      <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
        Expense
      </span>
    )
  }

  if (row?.source === "frp_utility") {
    return (
      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
        FRP invoice
      </span>
    )
  }

  if (row?.source === "ut_bill") {
    return (
      <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700">
        Standalone UT
      </span>
    )
  }

  if (row?.source === "wallet") {
    return (
      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
        Wallet
      </span>
    )
  }

  if (row?.source === "utility_adjustment") {
    return (
      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
        Utility adjustment
      </span>
    )
  }

  if (row?.source === "water_meter") {
    return (
      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
        Water meter
      </span>
    )
  }

  return null
}

// Money columns share one width so the table always fills the row exactly. The
// fewer of them there are, the more room the description gets.
const MONEY_COLUMN_WIDTHS = { 2: "20%", 3: "17%", 4: "14%" }

function TransactionsModal({ title, rows, loading, error, showBalanceColumn = false, onClose }) {
  const tableRows = withMonthlyRunningBalances(rows.map(normalizeTransactionTableRow))
  // Only a billing view records money paid out. Dropping the empty column
  // everywhere else gives the remaining figures room to fit on a phone.
  const showExpenseColumn = tableRows.some((row) => Number(row.expenseAmount || 0) > 0)
  const moneyColumnCount = 2 + (showExpenseColumn ? 1 : 0) + (showBalanceColumn ? 1 : 0)
  const moneyColumnWidth = MONEY_COLUMN_WIDTHS[moneyColumnCount] || "17%"
  const totals = tableRows.reduce(
    (acc, row) => ({
      charge: acc.charge + Number(row.chargeAmount || 0),
      expense: acc.expense + Number(row.expenseAmount || 0),
      payment: acc.payment + Number(row.paymentAmount || 0),
    }),
    { charge: 0, expense: 0, payment: 0 },
  )
  // The newest row carries the cash the month ended on.
  const closingBalance = tableRows[0]?.runningBalance || 0

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/60 p-3 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-black text-slate-900">Transactions</h3>
            <p className="truncate text-[11px] text-slate-500">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
          >
            <Icon name="x-lg" size="0.9rem" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="space-y-2 p-4">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="h-10 animate-pulse bg-slate-100" />
              ))}
            </div>
          ) : error ? (
            <p className="p-6 text-center text-sm text-red-600">{error}</p>
          ) : tableRows.length === 0 ? (
            <p className="p-8 text-center text-sm text-slate-500">No activity in this period.</p>
          ) : (
            <div className="border-t border-emerald-100 px-2 pb-3 sm:px-4">
              <div className="py-3">
                <div className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 sm:text-xs">
                  {tableRows.length} record{tableRows.length === 1 ? "" : "s"}
                </div>
              </div>
              <StatementTable
                rows={tableRows}
                rowKey={(row, index) => `${row.type}-${getUtilitySettlementId(row) || row.id || row.invoiceId || index}-${row.displayDate || index}`}
                emptyText="No activity in this period."
                summaryRow={[
                  <span className="text-slate-400">—</span>,
                  <span className="font-bold text-slate-900">Totals</span>,
                  <span className="font-bold text-red-600">{formatMoney(totals.charge)}</span>,
                  ...(showExpenseColumn
                    ? [<span className="font-bold text-rose-700">{formatMoney(totals.expense)}</span>]
                    : []),
                  <span className="font-bold text-emerald-700">{formatMoney(totals.payment)}</span>,
                  ...(showBalanceColumn
                    ? [
                        <span className={`font-bold ${closingBalance < 0 ? "text-rose-600" : "text-slate-900"}`}>
                          {formatMoney(closingBalance)}
                        </span>,
                      ]
                    : []),
                ]}
                columns={[
                  {
                    label: "Date",
                    width: moneyColumnCount > 3 ? "15%" : "16%",
                    render: (row) => formatDateWithTime(row.displayDate),
                  },
                  {
                    label: "Description",
                    align: "left",
                    render: (row) => (
                      <div>
                        <div className="font-medium text-slate-900">{row.description}</div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1">
                          <span className="capitalize text-slate-400">{row.type}</span>
                          <TransactionSourceTag row={row} />
                          {row.subtitle && <span className="text-slate-400">{row.subtitle}</span>}
                        </div>
                        {(row.eventType === "invoice" || row.eventType === "overdue_invoice" || row.eventType === "invoice_paid" || row.type === "invoice") && (
                          <div className="mt-1">
                            <UtilityInvoiceStatusBadge invoice={row} compact />
                          </div>
                        )}
                        {row?.source === "water_meter" && row?.meta && (
                          <div className="text-slate-400">
                            {Number(row.meta.currentReading || 0).toFixed(2)}-{Number(row.meta.cutOffReading || 0).toFixed(2)} ({Number(row.meta.allowedUnits || 0).toFixed(2)} u)
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    label: "Invoice",
                    width: moneyColumnWidth,
                    align: "right",
                    render: (row) =>
                      Number(row.chargeAmount || 0) > 0 ? (
                        <span className="font-semibold text-red-600">{formatMoney(row.chargeAmount)}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      ),
                  },
                  ...(showExpenseColumn
                    ? [
                        {
                          label: "Expenses",
                          width: moneyColumnWidth,
                          align: "right",
                          render: (row) =>
                            Number(row.expenseAmount || 0) > 0 ? (
                              <span className="font-semibold text-rose-700">{formatMoney(row.expenseAmount)}</span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            ),
                        },
                      ]
                    : []),
                  {
                    label: "Payment",
                    width: moneyColumnWidth,
                    align: "right",
                    render: (row) =>
                      Number(row.paymentAmount || 0) > 0 ? (
                        <span className="font-semibold text-emerald-700">{formatMoney(row.paymentAmount)}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      ),
                  },
                  ...(showBalanceColumn
                    ? [
                        {
                          label: "Balance",
                          width: moneyColumnWidth,
                          align: "right",
                          render: (row) => (
                            <span
                              className={`font-semibold ${
                                Number(row.runningBalance || 0) < 0 ? "text-rose-600" : "text-slate-700"
                              }`}
                            >
                              {formatMoney(row.runningBalance || 0)}
                            </span>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
export default MonthlySummaryPanel
